import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import _ from 'lodash'
import { Button } from 'components/styled/button'
import Question from 'components/question'
import VideoResponse from 'components/video-response/video-response'
import { mapLanguageToComponentProps } from 'i18n'
import { USE_FILE_UPLOAD } from 'features'
import CandidateTutorial from 'components/candidate-tutorials/candidate-tutorial'
import ThirdPartyScripts from 'careers/components/third-party-scripts'
import { PageContent } from 'components/styled/page/page-content'
import { Body, H3 } from 'components/styled/typography'
import ActionBarWrapper from 'components/action-bar-wrapper'
import { Box } from 'components/styled/containers/box'
import { CardGroup } from 'components/styled/card-group'
import { getPreviewActionBarRoot } from 'components/styled/page/preview-app-content'
import { Flex } from 'components/styled/containers/flex'

const propTypes = {
  application: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    jobAdQuickInterview: PropTypes.shape({}).isRequired,
  }).isRequired,
  jobAd: PropTypes.shape({
    formSetup: PropTypes.shape({
      questions: PropTypes.shape({}).isRequired,
    }).isRequired,
    questions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
  videoUploads: PropTypes.arrayOf(PropTypes.any).isRequired,
  context: PropTypes.string,
  onSubmitApplication: PropTypes.func,
  saveForLater: PropTypes.func,
  onVideoChange: PropTypes.func,
  onVideoRemove: PropTypes.func,
  onBack: PropTypes.func,
  highlightRequired: PropTypes.bool,
  recording: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool,
  continueLaterLoading: PropTypes.bool,
  company: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
}

const defaultProps = {
  context: 'careers',
  highlightRequired: false,
  onSubmitApplication: undefined,
  saveForLater: undefined,
  onVideoChange: undefined,
  onVideoRemove: undefined,
  onBack: undefined,
  submitLoading: false,
  continueLaterLoading: false,
}

const mapStateToProps = (state, ownProps) => ({
  application: state.application,
  jobAd: state.jobAd,
  company: ownProps.company || state.careers.company,
  recording: state.global.recording,
  videoUploads: state.global.videoUploads,
})

const StyledNotification = styled(Body)`
  color: ${({ theme }) => theme.color.error110};
  padding: ${({ theme }) => theme.space.s12};
  background-color: ${({ theme }) => theme.color.grey07};
  border-radius: ${({ theme }) => theme.space.s4};
`

const VideoContainer = styled(Box)`
  height: 100%;
`

const StyledCardGroupWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.space.s16};

  ${({ theme }) => theme.mediaQuery.screenS} {
    margin-bottom: ${({ theme }) => theme.space.s24};
  }
`

export function JobAdQuickInterview({
  jobAd,
  i18n,
  application,
  videoUploads,
  company,
  context,
  onSubmitApplication,
  saveForLater,
  onVideoChange,
  onVideoRemove,
  highlightRequired,
  onBack,
  recording,
  submitLoading,
  continueLaterLoading,
}) {

  const eventProps = {
    companyId: company._id,
    companyName: company.name,
    applicationId: application._id,
    language: jobAd && jobAd.language,
  }
  const getRequiredNotification = () => {
    if (highlightRequired) {
      return (
        <StyledNotification fontWeight={600}>
          {i18n.jobAdQuickInterview.requiredNote}
        </StyledNotification>
      )
    }
    return <Body>{i18n.jobAdQuickInterview.requiredNote}</Body>
  }

  return (
    <>
      <PageContent size="small" mt="s24">
        <H3>{i18n.jobAdQuickInterview.title}</H3>
        <Body>{i18n.jobAdQuickInterview.description}</Body>
        {jobAd.formSetup.questions.required ? getRequiredNotification() : null}
        <Body>{i18n.jobAdQuickInterview.reminderNote}</Body>
        <CandidateTutorial steps={[0, 1]} className="second" />

        {jobAd.questions.map(question => (
          <StyledCardGroupWrapper key={question._id}>
            <CardGroup minWidth="s320" key={question._id}>
              <VideoContainer>
                <Question
                  question={question}
                  className="radius"
                  locationId="jobAdQuickInterviewPage"
                  videoEventProps={eventProps}
                />
              </VideoContainer>
              <VideoContainer>
                <VideoResponse
                  className={`count-${jobAd.questions.length}`}
                  question={question}
                  useFileUpload={USE_FILE_UPLOAD}
                  uuid={application._id}
                  response={_.find(application.videoResponses, {
                    questionId: question._id,
                  })}
                  context="application"
                  onVideoRemove={() => {
                    onVideoRemove && onVideoRemove(question._id)
                  }}
                  onVideoChange={() => {
                    onVideoChange && onVideoChange(question._id, application)
                  }}
                  disableRecording={context === 'admin'}
                  locationId="jobAdQuickInterviewPage"
                  videoEventProps={eventProps}
                />
              </VideoContainer>
            </CardGroup>
          </StyledCardGroupWrapper>
        ))}
        <CandidateTutorial steps={[3]} className="second" />
      </PageContent>
      <ActionBarWrapper
        getActionBarRoot={
          context === 'admin' ? getPreviewActionBarRoot : undefined
        }
        actions={
          <>
            {!application.token && (
              <Flex style={{ flexGrow: 1 }}>
                <Button
                  text={i18n.jobAdQuickInterview.back}
                  onClick={onBack}
                  variant="secondary"
                  icon={jobAd.language === 'ar' ? 'chevron_right' : 'chevron_left'}
                  disabled={videoUploads.length > 0 || recording}
                />
              </Flex>
            )}
            <Button
              text={i18n.jobAdQuickInterview.saveForLater}
              icon="access_time"
              variant="secondary"
              onClick={saveForLater}
              loading={continueLaterLoading}
              disabled={videoUploads.length > 0 || recording}
              event={{
                locationId: 'jobAdQuickInterviewPage',
                id: 'applicationContinueLaterButton',
                properties: eventProps,
              }}
            />
            <Button
              text={i18n.jobAdQuickInterview.submit}
              icon="send"
              variant="primary"
              flippedIcon={jobAd.language === 'ar'}
              onClick={onSubmitApplication}
              loading={submitLoading}
              disabled={videoUploads.length > 0 || recording}
              event={{
                locationId: 'jobAdQuickInterviewPage',
                id: 'applicationSubmitButton',
                properties: {
                  companyId: company._id,
                  companyName: company.name,
                  applicationId: application._id,
                  language: jobAd && jobAd.language,
                },
              }}
            />
          </>
        }
      />
      {context === 'careers' && <ThirdPartyScripts />}
    </>
  )
}

JobAdQuickInterview.propTypes = propTypes
JobAdQuickInterview.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  JobAdQuickInterview
)
